import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";
const TICKET_STATUSES = ["Open", "In Progress", "Waiting on Customer", "Resolved", "Closed"];
const CLAIM_STATUSES = ["Submitted", "Document Verification", "Investigation", "Decision", "Resolved", "Rejected"];

export default function AdminSupport() {
  const [tab, setTab] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [claims, setClaims] = useState([]);
  const [notes, setNotes] = useState({});
  const [replies, setReplies] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = () => {
    api.get("/admin/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/admin/claims").then((r) => setClaims(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (kind, item) => {
    try {
      const payload = { status: item.status, note: notes[item.id] ?? item.note };
      if (kind === "tickets" && (replies[item.id] || "").trim()) payload.reply = replies[item.id].trim();
      await api.put(`/admin/${kind}/${item.id}`, payload);
      setReplies({ ...replies, [item.id]: "" });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const rows = tab === "tickets" ? tickets : claims;
  const statuses = tab === "tickets" ? TICKET_STATUSES : CLAIM_STATUSES;

  return (
    <div data-testid="admin-support" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Support Tickets & Claims</h1>
        <div className="flex gap-2" data-testid="admin-support-tabs">
          {[["tickets", `Tickets (${tickets.length})`], ["claims", `Claims (${claims.length})`]].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-support-tab-${t}`}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === t ? "bg-saffron text-white" : "border border-adminborder text-slate-300 hover:border-saffron"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <table className="w-full min-w-[980px] text-sm" data-testid="admin-support-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Ref</th><th className="px-5 py-3.5">Customer</th>
              <th className="px-5 py-3.5">{tab === "tickets" ? "Category" : "Issue / AWB"}</th>
              <th className="px-5 py-3.5">Description</th><th className="px-5 py-3.5">Raised</th>
              <th className="px-5 py-3.5">Note</th><th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-adminborder/50 text-slate-300" data-testid={`support-row-${r.ticket_id || r.claim_id}`}>
                <td className="px-5 py-3.5 font-mono font-bold text-saffron">{r.ticket_id || r.claim_id}</td>
                <td className="px-5 py-3.5">{tab === "tickets" ? r.name : `${r.sender || "—"} → ${r.receiver || "—"}`}
                  <span className="block text-[11px] text-slate-500">{r.phone}{r.email ? ` · ${r.email}` : ""}</span></td>
                <td className="px-5 py-3.5">{tab === "tickets" ? r.category : `${r.issue_type} · ${r.awb}`}</td>
                <td className="max-w-[260px] px-5 py-3.5">
                  <span className={`whitespace-pre-line text-xs text-slate-400 ${expanded[r.id] ? "" : "line-clamp-2"}`}
                        data-testid={`desc-${r.id}`}>{r.description}</span>
                  {(r.description || "").length > 90 && (
                    <button onClick={() => setExpanded({ ...expanded, [r.id]: !expanded[r.id] })}
                            data-testid={`desc-toggle-${r.id}`}
                            className="mt-0.5 block text-[10px] font-bold text-saffron hover:underline">
                      {expanded[r.id] ? "Show less" : "Show full"}
                    </button>
                  )}
                </td>
                <td className="px-5 py-3.5 text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3.5">
                  <input className={`${darkInput} min-w-[140px] py-1.5 text-xs`} data-testid={`note-${r.id}`}
                         placeholder="Team note" value={notes[r.id] ?? r.note ?? ""}
                         onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} />
                  {tab === "tickets" && (
                    <input className={`${darkInput} mt-1.5 min-w-[140px] py-1.5 text-xs`} data-testid={`reply-${r.id}`}
                           placeholder="Public reply to customer…" value={replies[r.id] ?? ""}
                           onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })} />
                  )}
                </td>
                <td className="px-5 py-3.5">
                  <select className={`${darkInput} py-1.5 text-xs`} data-testid={`status-${r.id}`} value={r.status}
                          onChange={(e) => { const v = e.target.value; const upd = (list) => list.map((x) => x.id === r.id ? { ...x, status: v } : x); tab === "tickets" ? setTickets(upd(tickets)) : setClaims(upd(claims)); }}>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3.5">
                  <button onClick={() => save(tab, r)} data-testid={`save-${r.id}`}
                          className="rounded-lg bg-saffron px-3 py-1.5 text-xs font-bold text-white hover:bg-saffron-hover">Save</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-500" data-testid="support-empty">No {tab} yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
